import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Activity, AlertTriangle, RefreshCw } from "lucide-react";

const PALLINO = { verde: "bg-emerald-500", arancione: "bg-amber-500", rosso: "bg-rose-500" };
const ETICHETTA = { verde: "app collegata adesso", arancione: "app non collegata", rosso: "problema tecnico" };

const quando = (iso) => {
  if (!iso) return "mai visto";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "adesso";
  if (m < 60) return `${m} min fa`;
  const o = Math.floor(m / 60);
  return o < 24 ? `${o} or${o === 1 ? "a" : "e"} fa` : `${Math.floor(o / 24)} giorni fa`;
};

/** Semaforo dei locali: chi sta lavorando, chi non si vede, chi ha un problema (e quale). */
export const StatoLocali = ({ client }) => {
  const [d, setD] = useState(null);
  const [aperto, setAperto] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await client.get("/admin/stato");
      setD(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stato dei locali non disponibile");
      setD({ rows: [], totals: {} });
    }
  }, [client]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);   // si aggiorna da solo ogni minuto
    return () => clearInterval(id);
  }, [load]);

  if (!d) return <p className="text-sm text-slate-400">Controllo lo stato dei locali…</p>;

  const rossi = d.rows.filter((r) => r.colore === "rosso");
  const altri = d.rows.filter((r) => r.colore !== "rosso");

  const riga = (r) => (
    <div key={r.tenant_id} className="rounded-lg border border-slate-800 bg-slate-900/60 p-3"
         data-testid={`stato-riga-${r.slug || r.tenant_id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${PALLINO[r.colore]} ${r.colore === "verde" ? "animate-pulse" : ""}`}
                data-testid={`stato-pallino-${r.colore}`} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">
              {r.tenant || r.slug}
              {r.demo && <span className="ml-1.5 text-[10px] uppercase text-slate-500">demo</span>}
            </p>
            <p className="text-xs text-slate-400">
              {ETICHETTA[r.colore]} · app vista {quando(r.last_seen)}
            </p>
            <p className="text-[11px] text-slate-500">
              ultimo PIN digitato {quando(r.last_pin)} · accesso al locale {quando(r.last_access)}
            </p>
          </div>
        </div>
        {r.problemi.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => setAperto(aperto === r.tenant_id ? null : r.tenant_id)}
                  data-testid={`stato-problemi-${r.slug || r.tenant_id}`}>
            <AlertTriangle className="mr-1.5 h-3.5 w-3.5" /> {r.problemi.length}
          </Button>
        )}
      </div>
      {aperto === r.tenant_id && (
        <ul className="mt-2 space-y-1.5 border-t border-slate-800 pt-2">
          {r.problemi.map((p, i) => (
            <li key={i} className="text-xs">
              <span className={p.grave ? "font-semibold text-rose-300" : "font-semibold text-amber-300"}>
                {p.testo}
              </span>
              {p.dettaglio && <span className="block break-all text-slate-500">{p.dettaglio}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <Card className="space-y-4 border-slate-800 bg-[#111827] p-5 text-slate-100" data-testid="stato-locali">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-head flex items-center gap-2 text-lg font-bold">
            <Activity className="h-4 w-4" /> Stato dei locali
          </h2>
          <p className="mt-0.5 text-xs text-slate-400">
            <span className="text-emerald-400">{d.totals.verde || 0} app collegate</span> ·{" "}
            <span className="text-amber-400">{d.totals.arancione || 0} non collegate</span> ·{" "}
            <span className="text-rose-400">{d.totals.rosso || 0} con problemi</span>
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="stato-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {rossi.length > 0 && (
        <div className="space-y-2" data-testid="stato-rossi">
          <p className="text-xs font-semibold uppercase tracking-wider text-rose-400">Da guardare subito</p>
          {rossi.map(riga)}
        </div>
      )}

      <div className="space-y-2">{altri.map(riga)}</div>

      <p className="text-[11px] text-slate-500">
        Verde = l'app del locale ha parlato col server negli ultimi 5 minuti: vuol dire «dispositivo
        acceso e collegato», non per forza «qualcuno sta lavorando» (un tablet aperto sul bancone
        basta). Per sapere chi lavora davvero guarda «ultimo PIN digitato». Arancione = app non
        collegata (di notte è normale). Rosso = errori del server, piatti fermi da oltre 45 minuti o
        locale sospeso; in quel caso ricevi anche un'email. Le tue visite di assistenza non contano.
      </p>
    </Card>
  );
};
